import { action, computed, makeObservable, observable } from "mobx";
import { AxiosResponse } from "axios";
import API from "../../utils/api";
import {SystemUser} from "../../models/users/user/system.user";
import { authService } from "../../services/auth/auth.service";
import { userService } from "../../services/users/user.service";
import { Group } from "../../models/users/group/group";
import * as Sentry from "@sentry/react";
import { CommonStore } from "../common/common.store";
import {LZ, SystemUserDepartment, SystemUserRole} from "../../models/users/user/system.user-with-attributes";
import {serverList} from "../../data/server-list";
import {parse, stringify} from "query-string";

export class AuthStore {

    static TOKEN = "tk0";
    static ACCOUNT = "def0";
    static BRANCH = "def1";
    static BRANCHES = "br0";

    @observable
    private _token: string = "";

    @observable
    private _defaultServer: string = "";

    @observable
    private _availableServers: string[] = [];

    @observable
    private _user: SystemUser | null = null;

    @observable
    private _access: number = 0;

    @observable
    private _attributes: string[] = [];

    @observable
    private _lz: LZ | null = null;

    @observable
    private _userGroup: Group | null = null;

    @observable
    private _userBranch: string = "";

    @observable
    private _originalBranch: string | undefined;

    @observable
    private _originalUsername: string | undefined;

    @observable
    public inited: boolean = false;

    @observable
    private _department: SystemUserDepartment | null = null;
    // private _history: any = history;

    @observable
    private _role: SystemUserRole | null = null;

    constructor(private commonStore: CommonStore) {
        makeObservable(this);
        this.logout = this.logout.bind(this);
    }

    public init () {
        this.token = localStorage.getItem(AuthStore.TOKEN) || "";
        this.access = parseInt(localStorage.getItem(AuthStore.ACCOUNT) || "") || 0;
        this.userBranch = localStorage.getItem(AuthStore.BRANCH) || "";

        if (this.userBranch) {
            this.defaultServer = serverList.get(this.userBranch as any);
            this.availableServers = serverList.getListByKeys((localStorage.getItem(AuthStore.BRANCHES) || "").split(","));
        } else {
            this.defaultServer  = serverList.getDefault();
        }

        this.initUnauthorizedInterceptor();
        // this.tryLoadFakeBranch();
        // this.tryLoadFakeUser();
        if (this.token !== "") {
            this.tryLoadUser();
        } else {
            this.setInited(true);
        }
    }

    @computed
    public get department(): SystemUserDepartment | null {
        return this._department;
    }

    public set department(value: SystemUserDepartment | null) {
        this._department = value;
    }

    @computed
    public get role(): SystemUserRole | null {
        return this._role;
    }

    public set role(value: SystemUserRole | null) {
        this._role = value;
    }

    @computed
    public get group(): Group | null {
        return this._userGroup;
    }

    private set userGroup(value: Group) {
        this._userGroup = value;
    }

    @computed
    public get user(): SystemUser | null {
        return this._user;
    }

    public set user(value: SystemUser | null) {
        this._user = value;
    }

    @computed
    public get attributes(): string[] {
        return this._attributes;
    }

    public set attributes(value: string[]) {
        this._attributes = value;
    }

    @computed
    public get lz(): LZ {
        if (!this._lz) {
            throw new Error('Failed to get LZ');
        }
        return this._lz;
    }

    public set lz(value: LZ | null) {
        this._lz = value;
    }

    @computed
    public get access(): number {
        return this._access;
    }

    public set access(value: number) {
        this._access = value;
    }

    @computed
    public get branch(): string {
        return this._userBranch;
    }

    public get userBranch(): string {
        return this._userBranch;
    }

    public set userBranch(value: string) {
        this._userBranch = value;
    }

    @computed
    public get originalBranch(): string | undefined {
        return this._originalBranch;
    }

    public set originalBranch(value: string | undefined) {
        this._originalBranch = value;
    }

    @computed
    public get originalUsername(): string | undefined {
        return this._originalUsername;
    }

    public set originalUsername(value: string | undefined) {
        this._originalUsername = value;
    }

    @computed
    public get isLoggedIn(): boolean {
        return null !== this._user;
    }

    @computed
    public get token(): string {
        return this._token;
    }

    public set token(token: string) {
        this._token = token;
    }

    public get defaultServer(): string {
        return this._defaultServer;
    }

    public set defaultServer(value: string) {
        this._defaultServer = value;
    }

    public get availableServers(): string[] {
        return this._availableServers;
    }

    public set availableServers(value: string[]) {
        this._availableServers = value;
    }

    @computed
    public get isUserFaked(): boolean {
        return !!this.originalUsername;
    }

    @computed
    public get isBranchFaked(): boolean {
        return !!this.originalBranch;
    }

    @action
    public async login(username: string, password: string) {
        const result = await authService.auth(username, password);

        localStorage.setItem(AuthStore.TOKEN,    result.token);
        localStorage.setItem(AuthStore.BRANCHES, result.branches.join(","));
        localStorage.setItem(AuthStore.BRANCH,   result.default.branch_id);
        localStorage.setItem(AuthStore.ACCOUNT,  result.default.account_id);

        this.token = result.token;
        this.defaultServer = serverList.get(result.default.branch_id as any);
        this.availableServers = serverList.getListByKeys(result.branches);
        this.access = parseInt(result.default.account_id);
        this.userBranch = result.default.branch_id;

        await this.loadUser();
    }

    public async switch(branch: string, account: string) {
        localStorage.setItem(AuthStore.BRANCH, branch);
        localStorage.setItem(AuthStore.ACCOUNT, account);
        window.location.reload();
    }

    public logout = async () => {

        localStorage.removeItem(AuthStore.TOKEN);
        localStorage.removeItem(AuthStore.BRANCHES);
        localStorage.removeItem(AuthStore.BRANCH);
        localStorage.removeItem(AuthStore.ACCOUNT);

        this._token = '';
        this._access = 0;
        this._availableServers = [];

        this.setUser(null);
        this.commonStore.clearStore();
        Sentry.configureScope((scope) => scope.setUser(null));
        this.commonStore.navigate("/login");
    };

    public async loadUser() {
        this.commonStore.clearStore();

        try {
            const userWithAttributes = await userService.getCurrentUserWithAttributes();

            this.setUser(userWithAttributes.user);
            Sentry.setUser({ email: userWithAttributes.user.email, id: userWithAttributes.user.id.toFixed(0) });
            this.setAttributes(userWithAttributes.attributes);
            this.setGroup(userWithAttributes.group);
            this.setBranch(userWithAttributes.branch);
            this.setUserDepartment(userWithAttributes.department);
            this.setUserRole(userWithAttributes.role);
            this.setOriginalBranch(userWithAttributes.originalBranch);
            this.setOriginalUsername(userWithAttributes.originalUser);
            this.setAccess(userWithAttributes.selectedAccess);
            this.setLZ(userWithAttributes.lz);
            this.setInited(true);
        } catch (e: any) {
            this.setInited(true);
        }
    }

    @action
    private setOriginalBranch(value: string | undefined) {
        this._originalBranch = value;
    }

    @action
    private setOriginalUsername(value: string | undefined) {
        this._originalUsername = value;
    }

    @action
    private setUser(value: SystemUser | null) {
        this.user = value;
    }

    @action
    private setBranch(value: string) {
        this.userBranch = value;
    }

    @action
    private setUserDepartment(value: SystemUserDepartment): void {
        this.department = value;
    }

    @action
    private setUserRole(value: SystemUserRole): void {
        this.role = value;
    }

    @action
    private setAttributes(values: string[]) {
        this.attributes = values;
    }

    @action
    private setLZ(lz: LZ) {
        this.lz = lz;
    }

    @action
    public setAccess(value: number | null) {
        if (this.access !== value) {
            this.access = value || 0;
        }
    }

    @action
    private setGroup(value: Group) {
        this.userGroup = value;
    }

    @action
    public setInited(value: boolean): void {
        this.inited = value;
    }

    private tryLoadUser(): void {
        this.loadUser();
    }

    private initUnauthorizedInterceptor() {

        API.interceptors.response.use(
            (value: AxiosResponse): AxiosResponse => {
                return value;
            },
            (error) => {
                if (
                    error.response?.status === 401
                    && window.location.pathname !== "/login"
                    && window.location.pathname !== "/forgot-password"
                    && window.location.pathname !== "/forgot-password/complete"
                    && window.location.pathname !== "/"
                ) {
                    this.logout();
                }
                return Promise.reject(error);
            }
        );
    }

    public generateUri = (baseUri: string, params: Record<string, string> | string = '') => {
        let obj: Record<string, any>;
        if (typeof params === 'string') {
            obj = parse(params, {arrayFormat: 'bracket'})
        } else if (params && typeof params === 'object' && !Array.isArray(params)) {
            obj = { ...params };
        } else {
            throw new TypeError('params must be an object or query string');
        }

        obj.token = String(this._token);
        obj.api_access = String(this._access);

       return 'https://' + this._defaultServer + baseUri + '?' + stringify(obj, {arrayFormat: 'bracket'})
    }
}
